import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import { Divider, Grid, Paper } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { formatDate } from 'date-fns'
import GridItem from 'src/components/grid-item/GridItem'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import PFScope from '../pf.scopes'
import { EnderecoBean, PessoaBean } from '../pf.types'

const LOG = Logger.get('PF-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    syncButton: {
        alignSelf: 'center'
    }
})

const addressColumns = static_build_address_columns()
const foneNumbersColumns = static_build_fone_numbers_columns()
const emailsColumns = static_build_emails_columns()

type PFViewProps = {
    className?: string
    scope: PFScope
}
class PFViewClass implements FCClassContext<PFViewProps> {
    // :: Fields
    scope!: PFScope
    sytles!: ReturnType<typeof useStyles>
    cpfInputRef!: React.MutableRefObject<HTMLInputElement | undefined>

    // :: emissors

    readonly emitOnFind = () => {
        this.scope.cpfSelected = this.cpfInputRef.current?.value || ''
        this.scope.onFind().catch(LOG.caught)
    }

    // :: Methods

    onSyncState({ scope }: PFViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: PFViewProps) {
        this.scope = bindUpdate(React, scope)
        this.cpfInputRef = React.useRef<HTMLInputElement>()

        const classes = this.sytles.classes

        return (
            <div className={clsx(classes.view, className)}>
                <Box display="flex" flexDirection="row" alignItems="baseline" gap="10px">
                    <TextField
                        className={classes.cnpjInput}
                        inputRef={this.cpfInputRef}
                        label="Cole ou escreva o CPF aqui"
                        variant="standard"
                    />
                    <Button variant="outlined" onClick={this.emitOnFind}>
                        Buscar
                    </Button>
                </Box>
                {this.scope.persons.map(this.createPerson)}
            </div>
        )
    }

    createPerson(person: PessoaBean, index: number) {
        return (
            <Paper key={index} elevation={3} style={{ padding: 10 }}>
                <Grid container spacing={1} paddingTop={2} paddingBottom={2} paddingLeft={5} paddingRight={5}>
                    <GridItem label="CPF" value={person.cpf} xs={4} />
                    <GridItem label="Nome" value={person.nome} xs={4} />
                    <GridItem
                        label="Data Nascimento"
                        value={person.dataDeNascimento ? formatDate(person.dataDeNascimento, 'dd/MM/yyyy') : ''}
                        xs={4}
                    />
                </Grid>

                <Divider textAlign="center">Detalhes</Divider>

                <Grid container spacing={1} paddingTop={2} paddingBottom={2} paddingLeft={5} paddingRight={5}>
                    {person.enderecos && person.enderecos.length && (
                        <Grid item xs={6}>
                            <DataGrid
                                rows={person.enderecos || []}
                                columns={addressColumns}
                                getRowId={static_getAddressRowId}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10
                                        }
                                    }
                                }}
                            />
                        </Grid>
                    )}
                    {person.telefones && person.telefones.length && (
                        <Grid item xs={6}>
                            <DataGrid
                                rows={person.telefones.map((item, idx) => ({ id: idx, value: item })) || []}
                                columns={foneNumbersColumns}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10
                                        }
                                    }
                                }}
                            />
                        </Grid>
                    )}
                    {person.emails && person.emails.length && (
                        <Grid item xs={6}>
                            <DataGrid
                                rows={person.emails.map((item, idx) => ({ id: idx, value: item })) || []}
                                columns={emailsColumns}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10
                                        }
                                    }
                                }}
                            />
                        </Grid>
                    )}
                </Grid>
            </Paper>
        )
    }
}

export const PFView = classToFComponent(PFViewClass, React)
export default PFView

function static_getAddressRowId(row: EnderecoBean) {
    return (row.logradouro ?? '') + ':' + (row.numero ?? '') + ':' + (row.cidade ?? '')
}

function static_build_address_columns(): GridColDef[] {
    const fieldNames: Record<keyof EnderecoBean, string> = {
        bairro: 'bairro',
        cidade: 'cidade',
        cep: 'cep',
        complemento: 'complemento',
        logradouro: 'logradouro',
        numero: 'numero',
        tipo: 'tipo',
        uf: 'uf'
    }
    return [
        {
            field: fieldNames.tipo,
            headerName: 'Tipo',
            width: 100
        },
        {
            field: fieldNames.logradouro,
            headerName: 'Rua',
            width: 300
        },
        {
            field: fieldNames.numero,
            headerName: 'Número',
            width: 50
        },
        {
            field: fieldNames.complemento,
            headerName: 'Complemento',
            width: 200
        },
        {
            field: fieldNames.bairro,
            headerName: 'Bairro',
            width: 100
        },
        {
            field: fieldNames.cidade,
            headerName: 'Cidade',
            width: 200
        },
        {
            field: fieldNames.cep,
            headerName: 'CEP',
            width: 80
        },
        {
            field: fieldNames.uf,
            headerName: 'UF',
            width: 50
        }
    ]
}

function static_build_emails_columns(): GridColDef[] {
    return [
        {
            field: 'value',
            headerName: 'Email',
            width: 300
        }
    ]
}

function static_build_fone_numbers_columns(): GridColDef[] {
    return [
        {
            field: 'value',
            headerName: 'Telefone',
            width: 300
        }
    ]
}
